import { format, startOfMonth, intervalToDuration, formatDuration } from 'date-fns';
import { enGB, pl } from 'date-fns/locale';
import { i18n, getLanguageFromLocale, getEffectiveLocale } from '../i18n/i18n';

function capitalizeFirstLetter(string) {
    return string.charAt(0).toUpperCase() + string.slice(1);
}

function getCurrentLocale() {
    const locales = {
        'en-GB': enGB,
        pl,
    };
    const lang = getLanguageFromLocale(getEffectiveLocale());
    const currentLocaleConfig = locales[lang] || locales['en-GB'];
    return currentLocaleConfig;
}

export default {
    createYearOptions({ from, to = new Date().getUTCFullYear() }) {
        const options = [];
        for (let year = from; year <= to; year += 1) {
            options.push({
                text: year,
                value: year,
            });
        }
        return options.reverse();
    },

    createMonthOptions() {
        const options = [];
        for (let month = 0; month <= 11; month += 1) {
            options.push({
                text: this.getMonthName(month),
                value: month + 1,
            });
        }
        return options;
    },

    getMonthName(monthNo) {
        const date = startOfMonth(new Date());
        date.setMonth(monthNo);
        return capitalizeFirstLetter(i18n.d(date, 'monthName'));
    },

    formatDate(value, dateFormat = 'dd.MM.yyyy') {
        const date = value instanceof Date ? value : new Date(value);
        return format(date, dateFormat || 'dd.MM.yyyy', { locale: getCurrentLocale() });
    },

    formatHour(value, timeFormat = 'HH:mm') {
        const date = value instanceof Date ? value : new Date(value);
        return format(date, timeFormat || 'HH:mm', { locale: getCurrentLocale() });
    },

    /**
     * Converts seconds into a formatted string value
     * @param value
     * @return {string}
     */
    secondsToString(value) {
        let seconds = value;
        const days = Math.floor(seconds / (3600 * 24));
        seconds -= days * 3600 * 24;
        const hours = Math.floor(seconds / 3600);
        seconds -= hours * 3600;
        const minutes = Math.floor(seconds / 60);
        seconds -= minutes * 60;
        seconds = Math.round(seconds);

        const parts = [];
        if (days > 0) {
            parts.push(`${days}${i18n.t('timeShort.days')}`);
        }
        if (hours > 0) {
            parts.push(`${hours}${i18n.t('timeShort.hours')}`);
        }
        if (minutes > 0) {
            parts.push(`${minutes}${i18n.t('timeShort.minutes')}`);
        }
        if (seconds > 0) {
            parts.push(`${seconds}${i18n.t('timeShort.seconds')}`);
        }

        return parts.length > 0 ? parts.join(' ') : `0${i18n.t('timeShort.seconds')}`;
    },

    formatMonth(value) {
        const date = value instanceof Date ? value : new Date(value);
        return this.getMonthName(date.getMonth());
    },

    formatDateForUrl(value) {
        const date = value instanceof Date ? value : new Date(value);
        return format(date, 'yyyy-MM-dd');
    },

    getDurationBetweenDates(startDate, endDate) {
        // Upewniamy się, że daty są w odpowiedniej kolejności: startDate <= endDate
        if (startDate > endDate) {
            [startDate, endDate] = [endDate, startDate]; // eslint-disable-line no-param-reassign
        }
        const locale = getCurrentLocale();
        const duration = intervalToDuration({
            start: startDate,
            end: endDate,
        });
        // Można dodać inne np. 'hours', 'minutes'...
        let formatted = formatDuration(duration, {
            locale,
            delimiter: ', ',
            format: ['years', 'months', 'days'],
            zero: true,
        });
        if (!formatted) {
            formatted = formatDuration({ years: 0, months: 0, days: 0 }, { locale });
        }
        // Mapowanie słów, które w oryginale występują bez "1"
        const singleFormMap = {
            rok: '1 rok',
            miesiąc: '1 miesiąc',
            dzień: '1 dzień',
            tydzień: '1 tydzień',
            godzina: '1 godzina',
            minuta: '1 minuta',
            sekunda: '1 sekunda',
        };
        const patchedParts = formatted.split(', ').map(part => singleFormMap[part] || part);
        const patchedString = patchedParts.join(', ');
        return patchedString;
    },
};
